import { useEffect, useState, useContext, useCallback } from "react";
import useAxiosRequests from "../../../hooks/useAxiosRequest";
import {
  Autocomplete, Box, CircularProgress, TextField, Tooltip, Typography, Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import ExternalRoutes from "../../../config/ExternalRoutes";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { IconButton, Menu, MenuItem } from "@mui/material";
import ConfirmationDialog from "../ConfirmationDialog";
import DocumentUpdate from "./DocumentUpdate";
import { useLocation } from "react-router";
import { DOCUMENT_TYPE_LABELS } from "../../../utils/Constants";
import * as Yup from "yup";
import { useFormik } from "formik";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from "dayjs";
import "dayjs/locale/fr";
import { MessageContext } from "../../../context/MessageContext";
import ShowUser from "../users/ShowUser";
import ShowDocument from "./ShowDocument";
import { safeDate } from "../../../utils/FormatDate";
dayjs.locale("fr");

const RenderCellUser = ({ row }) => {
  const firstUser = row.conUser ? row.conUser : {};
  const [showUser, setShowUser] = useState(false);

  const hanldeViewUser = () => {
    setShowUser(!showUser);
  }

  return (
    <>
      <Tooltip title={`${firstUser.email || ""}}`}>
        <span className="kl-link-admin" onClick={hanldeViewUser}>{`${firstUser.email || ""}`}</span>
      </Tooltip>
      <ShowUser open={showUser} handleClose={hanldeViewUser} userData={firstUser} />
    </>
  );
}

export const StatusEditPopup = ({ openPopup, handleClose, selectedRow }) => {
  const validationSchema = Yup.object({
    doc_status: Yup.string()
      .required("Veuillez sélectionner un statut.")
      .typeError("Le statut doit être un text valide."),
  });

  const formik = useFormik({
    initialValues: {
      doc_status: selectedRow?.conDocumentStatus?.docStName || "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      handleUpdateStatus(values.doc_status);
    },
  });
  const { sendRequest } = useAxiosRequests();
  const { showError, showSuccess, setReaload } = useContext(MessageContext);
  const [docStatus, setDocStatus] = useState([]);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const fetchDocStatus = useCallback(async () => {
    try {
      const response = await sendRequest({
        method: "GET",
        url: ExternalRoutes.doc_status.list,
      });

      if (response && Array.isArray(response.member)) {
        setDocStatus(response.member);
      } else {
        setDocStatus([]);
      }
    } catch (error) {
      setDocStatus([]);
    }
  }, [sendRequest]);

  const handleUpdateStatus = async (newStatusId) => {
    setUpdatingStatus(true);
    try {
      const response = await sendRequest({
        method: "POST",
        url: ExternalRoutes.document.update_status + selectedRow.id,
        body: { "doc_status": newStatusId },
        headers: {
          "Content-Type": "application/json",
        },
      });
      console.log(ExternalRoutes.document.update_status + selectedRow.id);
      console.log(newStatusId);
      if (response.status === 'error') {
        showError("Une erreur est survenue lors de la modification.");
      }
      showSuccess("Status mis à jour avec succès.");
    } catch (error) {
      showError("Une erreur est survenue lors de la modification.");
    } finally {
      setUpdatingStatus(false);
      handleClose()
      setReaload(true);
    }
  };

  useEffect(() => {
    if (openPopup) {
      fetchDocStatus()
    }
  }, [openPopup])

  return (
    <Dialog open={openPopup} onClose={handleClose}>

      <DialogTitle>Modifier le statut</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          select
          margin="normal"
          label="Statut"
          name="doc_status"
          value={
            docStatus.some(s => s.docStName === formik.values.doc_status)
              ? formik.values.doc_status
              : ''
          }
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.doc_status && Boolean(formik.errors.doc_status)}
          helperText={formik.touched.doc_status && formik.errors.doc_status}
        >
          {docStatus.map((status, index) => (
            <MenuItem key={status.id || status.docStName + '-' + index} value={status.docStName}>
              {status.docStName}
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={updatingStatus}>
          Annuler
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={updatingStatus || !formik.isValid}
          onClick={formik.handleSubmit}
          startIcon={updatingStatus ? <CircularProgress size={20} /> : null}
        >
          {updatingStatus ? "Enregistrement..." : "Enregistrer"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const RenderCellStatus = ({ row }) => {
  const [openPopup, setOpenPopup] = useState(false);

  const handleStatusClick = () => {
    setOpenPopup(!openPopup);
  }

  return (
    <>
      <Button
        color="default"
        variant="outlined"
        onClick={() => handleStatusClick(row)}
      >
        {row.conDocumentStatus?.docStName || "Statut"}
      </Button>
      <StatusEditPopup openPopup={openPopup} handleClose={handleStatusClick} selectedRow={row} />
    </>
  )
}

const columns = (handleEditClick, handleShowDocument) => [
  {
    field: "docName",
    headerName: "Nom du document",
    flex: 0.15,
    sortable: true,
    minWidth: 120,
    renderCell: (params) => (
      <Tooltip title={params.value || ""}>
        <span>{params.value || ""}</span>
      </Tooltip>
    ),
  },
  {
    field: "conUser",
    headerName: "Utilisateur",
    flex: 0.15,
    sortable: true,
    minWidth: 120,
    valueGetter: (params) => {
      return params ? `${params.usrFirstname} ${params.usrLastname}` : "";
    },
    renderCell: RenderCellUser
  },
  {
    field: "docDateUpload",
    headerName: "Date Upload",
    flex: 0.15,
    sortable: true,
    minWidth: 120,
    renderCell: (params) => {
      const date = params.value ? new Date(params.value) : null;
      const formattedDate = date
        ? `${date.toLocaleDateString('fr-FR')} ${date.getHours()}:${date.getMinutes().toString().padStart(2, '0')}`
        : "";
      return (
        <Tooltip title={formattedDate}>
          <span>{formattedDate}</span>
        </Tooltip>
      );
    },
  },
  {
    field: "conDocumentStatus.docStName",
    headerName: "Statut",
    flex: 0.15,
    renderCell: RenderCellStatus
  },
  {
    field: "isSubmit",
    headerName: "Soumettre",
    flex: 0.15,
    sortable: true,
    minWidth: 120,
    renderCell: (params) => (

      <div style={{ display: 'flex', justifyContent: 'center' }}>
        {params.value ? (
          <Chip label="Soumis" color="warning" size="small" />
        ) : ''}
      </div>
    ),
  },
  {
    field: "docMyUnisoftId",
    headerName: "My unisoft",
    flex: 0.15,
    sortable: true,
    minWidth: 120,
    renderCell: (params) => (
      <Tooltip title={params.value || ""}>
        <span>{params.value || ""}</span>
      </Tooltip>
    ),
  },
  {
    field: "actions",
    headerName: "Actions",
    flex: 0.15,
    sortable: false,
    filterable: false,
    minWidth: 120,
    renderCell: (params) => (
      <RowActionsMenu
        id={params.id}
        rowData={params}
        onEditClick={handleEditClick}
        onShowDetail={handleShowDocument}
      />
    ),
  },
];

const RowActionsMenu = ({ id, rowData, onEditClick, onShowDetail }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const { sendRequest } = useAxiosRequests();

  const handleRowOptionsClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleRowOptionsClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    onEditClick(rowData);
    handleRowOptionsClose();
  };

  const handleShow = () => {
    onShowDetail(rowData.row.docName);
    handleRowOptionsClose();
  };

  const handleDelete = async () => {
    try {
      await sendRequest({
        method: "DELETE",
        url: `${ExternalRoutes.document.conDocuments}/${id}`,
      });
    } catch (err) {
    }
    setIsDeleteDialogOpen(false);
    handleRowOptionsClose();
  };

  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true);
    handleRowOptionsClose();
  };

  return (
    <>
      <IconButton size="small" onClick={handleRowOptionsClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleRowOptionsClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ style: { minWidth: "8rem", zIndex: 1300 } }}
      >
        <MenuItem onClick={handleEdit}>Modifier</MenuItem>
        <MenuItem onClick={handleShow}>Détail</MenuItem>
        <MenuItem onClick={handleDeleteClick}>Supprimer</MenuItem>
      </Menu>

      <ConfirmationDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title="Confirmer la suppression"
        message="Êtes-vous sûr de vouloir supprimer ce document ? Cette action est irréversible."
      />
    </>
  );
};

const DocumentList = () => {
  const { state } = useLocation();
  const { society, firstUser } = state || [];
  const { sendRequest } = useAxiosRequests();
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortModel, setSortModel] = useState([]);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [status, setStatus] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [docStatusFilter, setDocStatusFilter] = useState(null);
  const [users, setUsers] = useState([]);
  const [user, setUser] = useState(firstUser ?? null);
  const [societyFilters, setSocietyFilters] = useState([]);
  const [societyFilter, setSocietyFilter] = useState(society ?? null);
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState('')
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [docStatus, setDocStatus] = useState([]);
  const { reaload, setReaload } = useContext(MessageContext);
  const [queryUser, setQueryUser] = useState();
  const [querySociety, setQuerySociety] = useState();
  const [openShowDocument, setOpenShowDocument] = useState(false);
  const [selectedDocName, setSelectedDocName] = useState(null);


  const fetchDocStatus = useCallback(async () => {
    try {
      const response = await sendRequest({
        method: "GET",
        url: ExternalRoutes.doc_status.list,
      });

      if (response && Array.isArray(response.member)) {
        setDocStatus(response.member);
      } else {
        setDocStatus([]);
      }
    } catch (error) {
      setDocStatus([]);
    }
  }, [sendRequest]);



  const [openUpdateDialog, setOpenUpdateDialog] = useState(false);
  const [selectedDocumentData, setSelectedDocumentData] = useState(null);


  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await sendRequest({
        method: "GET",
        url: ExternalRoutes.document.conDocuments,
        config: {
          params: {
            order: sortModel.length > 0 ? { [sortModel[0].field]: sortModel[0].sort } : {},
            page: paginationModel.page + 1,
            itemsPerPage: paginationModel.pageSize,
            query: searchQuery,
            'society.id': societyFilter?.id,
            'docType': selectedType,
            'conDocumentStatus.docStName': docStatusFilter,
            'conUser.email': user?.email,
            'docDateUpload[after]': startDate,
            'docDateUpload[before]': endDate
          },
        },
      });

      if (response) {
        setDocuments(response.member);
        setTotalDocuments(response.totalItems);
      }
    } catch (error) {
    } finally {
      setLoading(false);
      setReaload(false);
    }
  }, [docStatusFilter, endDate, paginationModel.page,
    paginationModel.pageSize, searchQuery, selectedType, sendRequest, societyFilter?.id,
    sortModel, startDate, user?.email
  ]);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search)
    const docName = queryParams.get('doc')
    if (docName) {
      setOpenShowDocument(true);
      setSelectedDocName(docName);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [sortModel, paginationModel, searchQuery,
    selectedType, docStatusFilter, user, societyFilter,
    startDate, endDate, reaload]);

  const handleSortModelChange = (newSortingModel) => {
    setLoading(true);
    setSortModel(newSortingModel);
  };

  const handlePaginationChange = (newPaginationModel) => {
    setLoading(true);
    setPaginationModel(newPaginationModel);
  };


  const handleOpenUpdateDialog = (data) => {
    setSelectedDocumentData(data);
    setOpenUpdateDialog(true);
  };

  const handleCloseUpdateDialog = () => {
    setOpenUpdateDialog(false);
    setSelectedDocumentData(null);
  };

  const fetchDataStatus = useCallback(async () => {
    try {
      const response = await sendRequest({
        method: "GET",
        url: ExternalRoutes.doc_status.list,
      })
      if (response) {
        setStatus(response.member);
      }
    } catch (error) {
    }
  }, [sendRequest]);

  const fetchDataUser = useCallback(async () => {
    try {
      const response = await sendRequest({
        method: "GET",
        url: ExternalRoutes.users.list,
        config: {
          params: {
            query: queryUser
          }
        }
      })
      if (response) {
        setUsers(response.member);
      }
    } catch (error) {
    }
  }, [sendRequest, queryUser]);

  const fetchDataSociety = useCallback(async () => {
    try {
      const response = await sendRequest({
        method: "GET",
        url: ExternalRoutes.society.list,
        config: {
          params: {
            query: querySociety
          }
        }
      })
      if (response) {
        setSocietyFilters(response.member);
      }
    } catch (error) {
    }
  }, [sendRequest, querySociety]);

  useEffect(() => {
    fetchDataStatus();
    fetchDocStatus();
  }, [])

  useEffect(() => {
    fetchDataUser();
  }, [queryUser]);

  useEffect(() => {
    fetchDataSociety();
  }, [querySociety]);

  const handleChangeType = (event, selectedOption) => {
    setLoading(true);
    if (selectedOption) {
      setSelectedType(selectedOption.value);
    } else {
      setSelectedType(null);
    }
  };

  const DOCUMENT_TYPE_OPTIONS = Object.entries(DOCUMENT_TYPE_LABELS).map(
    ([value, label]) => ({ value, label })
  );

  const handleChangeStatus = (event, value) => {
    setLoading(true);
    if (value) {
      setDocStatusFilter(value.docStName);
    } else {
      setDocStatusFilter(null);
    }
  }

  const handleChangeUser = (event, value) => {
    setLoading(true);
    if (value) {
      setUser(value);
    } else {
      setUser(null);
    }
  }

  const handleChangeSociety = (event, value) => {
    setLoading(true);
    if (value) {
      setSocietyFilter(value);
    } else {
      setSocietyFilter(null);
    }
  }

  const handleChangeStartDate = (newDate) => {
    setLoading(true);
    if (newDate && newDate.isValid()) {
      setStartDate(newDate);
    } else {
      setStartDate(null);
    }
  }
  const handleChangeEndDate = (newDate) => {
    setLoading(true);
    if (newDate && newDate.isValid()) {
      setEndDate(newDate);
    } else {
      setEndDate(null);
    }
  }

  const handleOpenShowDocument = (docName) => {
    setSelectedDocName(docName);
    setOpenShowDocument(true);
  };

  const handleCloseShowDocument = () => {
    setOpenShowDocument(false);
    setSelectedDocName(null);
  };

  const searchQueryHandler = (event) => {
    setLoading(true);
    const value = event.target.value;
    setSearchQuery(value);
  } 

  return (
    <>
      <Box
        className="kl-admin-header"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: "bold" }}>
          📄 Liste des Documents
        </Typography>
      </Box>

      <TextField
        fullWidth
        size="small"
        variant="outlined"
        placeholder="🔍 Rechercher..."
        value={searchQuery}
        onChange={searchQueryHandler}
        sx={{ mb: 2 }}
      />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          mb: 2,
          gap: 2,
        }}
      >
        <LocalizationProvider adapterLocale="fr" dateAdapter={AdapterDayjs}>
          <DatePicker
            value={startDate || null}
            label="Date de Début"
            dateFormat='dd MMMM yyyy'
            onChange={handleChangeStartDate}
            renderInput={(params) => <TextField {...params} />}
          />
        </LocalizationProvider>

        <LocalizationProvider adapterLocale="fr" dateAdapter={AdapterDayjs}>
          <DatePicker
            value={endDate || null}
            label="Date de Fin"
            dateFormat='dd MMMM yyyy'
            onChange={handleChangeEndDate}
            renderInput={(params) => <TextField {...params} />}
          />
        </LocalizationProvider>

        <Autocomplete
          disablePortal
          options={DOCUMENT_TYPE_OPTIONS}
          sx={{ minWidth: 250 }}
          getOptionLabel={(option) => option.label}
          onChange={handleChangeType}
          renderInput={(params) => <TextField {...params} label="Type" />}
        />

        <Autocomplete
          disablePortal
          options={status}
          sx={{ minWidth: 250 }}
          getOptionLabel={(option) => option.docStName}
          onChange={handleChangeStatus}
          renderInput={(params) => <TextField {...params} label="Status" />}
        />

        <Autocomplete
          value={user}
          disablePortal
          options={users}
          sx={{ minWidth: 250 }}
          getOptionLabel={(option) =>
            option.usrLastname && option.usrFirstname
              ? option.usrLastname + ' ' + option.usrFirstname
              : option.email
          }
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={handleChangeUser}
          onInputChange={(e, value) => setQueryUser(value)}
          // onBlur={(e) => setQueryUser(e)}
          renderInput={(params) => <TextField {...params} label="Utilisateur" />}
          renderOption={(props, option, index) => (
            <li {...props} key={option.id || option.email + '-' + index}>
              {option.usrLastname && option.usrFirstname
                ? `${option.usrLastname} ${option.usrFirstname}`
                : option.email}
            </li>
          )}
        />

        <Autocomplete
          value={societyFilter}
          disablePortal
          options={societyFilters}
          sx={{ minWidth: 250 }}
          getOptionLabel={(option) => option.sctName}
          onChange={handleChangeSociety}
          onInputChange={(e, value) => setQuerySociety(value)}
          renderInput={(params) => <TextField {...params} label="société" />}
          renderOption={(props, option, index) => (
            <li {...props} key={option.id || option.sctName + '-' + index}>
              {option.sctName}
            </li>
          )}
        />

      </Box>
      <DataGrid
        disableColumnMenu
        pagination
        rows={documents}
        loading={loading}
        rowCount={totalDocuments}
        columns={columns(handleOpenUpdateDialog, handleOpenShowDocument)}
        sortingMode="server"
        paginationMode="server"
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        autoHeight
        disableRowSelectionOnClick
        pageSizeOptions={[10, 25, 50]}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationChange}
        componentsProps={{
          pagination: {
            labelRowsPerPage: "Lignes par page",
            labelDisplayedRows: ({ from, to, count }) =>
              `${from}-${to} sur ${count !== -1 ? count : `plus de ${to}`}`,
          },
        }}
        localeText={{
          noRowsLabel: 'Aucune donnée disponible',
        }}
      />

      <DocumentUpdate
        open={openUpdateDialog}
        handleClose={handleCloseUpdateDialog}
        documentData={selectedDocumentData}
        onUpdated={fetchData}
      />

      {openShowDocument && (
        <ShowDocument
          open={openShowDocument}
          handleClose={handleCloseShowDocument}
          docName={selectedDocName}
          onUpdated={fetchData}
        />
      )}
    </>
  );
};

export default DocumentList;